/* eslint-disable @typescript-eslint/no-non-null-assertion */

'use client';

import { c as _c } from "react/compiler-runtime";
import type { I18n } from '@funkey/locales';
import { RadioButton, FormGroup, FormInputErrors, Button, FormLabel, Textarea } from '~/components/primitives';
import { useEffect, useMemo } from 'react';
import { graphql } from '@funkey/types/graphql/gql';
import { sleep, std } from '@funkey/shared/utils';
import { z } from 'zod';
import { useFormStatus } from 'react-dom';
import { renderToStaticMarkup } from 'react-dom/server';
import { COUNTRY_CODE_TRANSLATIONS, I18N_CONF } from '@funkey/locales/config';
import posthog from 'posthog-js';
import { api } from '~/modules';
import useNotifications from '~/features/notifications/useNotifications';
import { useI18n } from '~/features/i18n';
import { useStateObject } from '~/utils/react';
import { useFormState, type FormState } from '~/modules/form';
import { CheckoutAccountFormFields } from '~/features/cart/checkout/account/CheckoutAccountPageForm';
import { InternalEmailDataTable } from '~/features/mail/components';
import { GTM } from '~/features/google/tagmanager';
import { useReCAPTCHA } from '~/features/form/recaptcha/client';
import { QuoteRequestFormFields } from '~/features/cart/checkout/QuoteRequestForm';
import { useTRPC } from '~/modules/trpc/client';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
export const ContactFormSectionForm: React.FC = _props => {
  const i18n = useI18n();
  const recaptcha = useReCAPTCHA();

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const selectedSubjectType = useStateObject<SubjectTypes.Variant>('Quote request');
  const SUBJECT_TYPES = useMemo(() => SubjectTypes.VARIANT_META(i18n), [i18n.locale]);
  const selectedSubjectMeta = SUBJECT_TYPES[selectedSubjectType.value];
  return <>
            <FormGroup label={i18n._('What can we help you with')} variant="no_spacing" htmlFor="subject" className="col-span-2 mb-10 mt-1 text-primary">
                <div className="flex flex-col gap-3">
                    {std.Object.entries(SUBJECT_TYPES).map(([value, meta]) => <div key={value} className="flex items-center gap-3">
                            <RadioButton id={value} defaultChecked={selectedSubjectType.value === value} name="subject" value={value} onChange={() => selectedSubjectType.set(value)} />
                            <FormLabel htmlFor={value}>{meta.label}</FormLabel>
                        </div>)}
                </div>
            </FormGroup>

            <selectedSubjectMeta.Form i18n={i18n} variant={selectedSubjectType.value} subject={selectedSubjectMeta.label} recaptcha={recaptcha} />
        </>;
};
namespace ContactForm {
  export const Form: React.FC<{
    onSubmit: () => void;
    children?: React.ReactNode;
  }> = props => {
    const $ = _c(5);
    let t0;
    if ($[0] !== props) {
      t0 = async () => props.onSubmit();
      $[0] = props;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    let t1;
    if ($[2] !== props.children || $[3] !== t0) {
      t1 = <form className="flex flex-col gap-10" action={t0} data-testid="contact-form">{props.children}</form>;
      $[2] = props.children;
      $[3] = t0;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  };
  export const Footer: React.FC<{
    i18n: I18n;
    recaptcha: ReturnType<typeof useReCAPTCHA>;
    disabled?: boolean;
  }> = props => {
    const $ = _c(11);
    const form = useFormStatus();
    let t0;
    if ($[0] !== props.recaptcha.Component) {
      t0 = <div className="col-span-2"><props.recaptcha.Component /></div>;
      $[0] = props.recaptcha.Component;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    const t1 = !props.recaptcha.isValidated || props.disabled;
    const t2 = form.pending;
    let t3;
    if ($[2] !== props.i18n) {
      t3 = props.i18n._("Send");
      $[2] = props.i18n;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] !== form.pending || $[5] !== t1 || $[6] !== t3) {
      t4 = <div className="mb-20"><Button variant="tertiary" disabled={t1} iconBefore="arrow-right" isLoading={t2} type="submit" data-testid="contact-form-submit">{t3}</Button></div>;
      $[4] = form.pending;
      $[5] = t1;
      $[6] = t3;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    let t5;
    if ($[8] !== t0 || $[9] !== t4) {
      t5 = <>{t0}{t4}</>;
      $[8] = t0;
      $[9] = t4;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    return t5;
  };
}
export namespace QuestionForm {
  export type FormValues = CheckoutAccountFormFields.FormValues & {
    message: string;
    country: I18N_CONF.Country;
  };
  export const Component: React.FC<{
    i18n: I18n;
    subject: string;
    variant: SubjectTypes.Variant;
    showCountryLocationField: boolean;
    recaptcha: ReturnType<typeof useReCAPTCHA>;
  }> = props => {
    const cache = QuoteRequestFormFields.useValuesCache();
    const formState = useFormState<QuestionForm.FormValues>({
      ...cache.value,
      message: (cache.value as any).message ?? '',
      country: (cache.value as any).country ?? props.i18n.country
    }, useMemo(() => QuestionForm.VALIDATION(props.i18n), [props.i18n]));
    useEffect(() => {
      cache.sync({
        ...cache.value,
        ...formState.values
      });
    }, [formState.values]);
    const sendEmail = useSendMailMutation(props.i18n, () => {
      const trackingData = {
        ...formState.values,
        variant: props.variant,
        subject: props.subject
      };
      GTM.setUserData(formState.values.email);
      GTM.sendEvent('funkeyContactFormSubmit', trackingData);
      posthog.capture('contact.submit', trackingData);
      formState.resetForm();
    });
    return <ContactForm.Form onSubmit={() => {
      if (!formState.validate()) {
        // eslint-disable-next-line no-console
        console.error(formState.errors);
        return;
      }
      return sendEmail.mutateAsync({
        firstName: formState.values.firstName,
        fromEmail: 'info@funkey.be',
        subject: `Contact: ${props.subject}`
      }, {
        [props.i18n._('contact.form.first_name')]: formState.values.firstName,
        [props.i18n._('contact.form.last_name')]: formState.values.lastName,
        [props.i18n._('contact.form.email')]: formState.values.email,
        [props.i18n._('contact.form.phone_number')]: formState.values.phone,
        [props.i18n._('contact.form.vat_number')]: formState.values.vatNumber,
        [props.i18n._('contact.form.country')]: props.showCountryLocationField === true ? formState.values.country : undefined,
        [props.i18n._('contact.form.message')]: formState.values.message
      });
    }}>
                <CheckoutAccountFormFields.FieldSet formState={formState.$subform<CheckoutAccountFormFields.FormValues>()} />

                <fieldset className="col-span-2">
                    <FormGroup variant="no_spacing" label={props.i18n._('contact.form.message')} htmlFor="message" className="mt-1 w-full">
                        <Textarea className="mt-1" value={formState.values.message} rows={5} onChange={e => formState.handleChange('message', e.currentTarget.value)} placeholder={props.i18n._('contact.form.message.placeholder')} />
                        <FormInputErrors errors={formState.getErrorMessages('message')} />
                    </FormGroup>
                </fieldset>

                {props.showCountryLocationField && <FormGroup variant="no_spacing" label={props.i18n._('contact.form.country')} className="col-span-2 gap-3" classNameLabel="mb-3">
                        <div className="flex gap-6">
                            {I18N_CONF.COUNTRIES.map(countryCode => <div key={countryCode} className="flex items-center gap-2">
                                    <RadioButton id={countryCode} name="location" defaultChecked={formState.values.country === countryCode} value={countryCode} onChange={() => formState.handleChange('country', countryCode)} />
                                    <FormLabel htmlFor={countryCode}>
                                        {COUNTRY_CODE_TRANSLATIONS[countryCode](props.i18n)}
                                    </FormLabel>
                                </div>)}
                        </div>
                        <FormInputErrors errors={formState.getErrorMessages('country')} />
                    </FormGroup>}

                <ContactForm.Footer i18n={props.i18n} recaptcha={props.recaptcha} />
            </ContactForm.Form>;
  };
  export const VALIDATION = (i18n: I18n): FormState.Validation<QuestionForm.FormValues> => ({
    ...CheckoutAccountFormFields.FORM_VALIDATION(i18n),
    message: {
      validate: value => z.string().min(1).safeParse(value).success,
      message: i18n._('Message must contain minimum 1 character')
    }
  });
  const SEND_EMAIL_MUTATION = graphql(`
        mutation sendEmail($input: SendEmailInput!) {
            sendEmail(input: $input) {
                success
            }
        }
    `);
  export function useSendMailMutation(i18n, onSuccess) {
    const $ = _c(11);
    const notification = useNotifications();
    let t0;
    if ($[0] !== i18n || $[1] !== notification || $[2] !== onSuccess) {
      t0 = {
        throwOnError: false,
        onSuccess(data) {
          if (data.sendEmail?.success) {
            notification.dispatch({
              type: "SUCCESS",
              title: i18n._("Success"),
              message: i18n._("contact.email.success")
            });
            onSuccess();
          } else {
            notification.dispatch({
              type: "ERROR",
              title: i18n._("Error"),
              message: i18n._("contact.email.error")
            });
          }
        },
        onError(error) {
          console.error(error);
          notification.dispatchError();
        }
      };
      $[0] = i18n;
      $[1] = notification;
      $[2] = onSuccess;
      $[3] = t0;
    } else {
      t0 = $[3];
    }
    const sendEmail = api.useGraphqlMutation(SEND_EMAIL_MUTATION, t0);
    let t1;
    if ($[4] !== sendEmail) {
      t1 = std.Object.omit(sendEmail, ["mutate"]);
      $[4] = sendEmail;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    let t2;
    if ($[6] !== sendEmail) {
      t2 = (emailInfo, body) => sendEmail.mutateAsync({
        input: {
          subject: emailInfo.subject,
          firstName: emailInfo.firstName,
          from: "info@funkey.be",
          email: "info@funkey.be",
          body: renderToStaticMarkup(<InternalEmailDataTable entries={body} />)
        }
      });
      $[6] = sendEmail;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] !== t1 || $[9] !== t2) {
      t3 = {
        ...t1,
        mutateAsync: t2
      };
      $[8] = t1;
      $[9] = t2;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    return t3;
  }
}
namespace QuoteRequestForm {
  export type FormValues = QuoteRequestFormFields.FormValues;
  export const Component: React.FC<{
    i18n: ReturnType<typeof useI18n>;
    formState: FormState.Subform<FormValues>;
    subject: string;
    recaptcha: ReturnType<typeof useReCAPTCHA>;
  }> = props => {
    const $ = _c(24);
    const notification = useNotifications();
    const router = useRouter();
    const trpc = useTRPC();
    let t0;
    if ($[0] !== trpc.deal.create) {
      t0 = trpc.deal.create.mutationOptions();
      $[0] = trpc.deal.create;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    let t1;
    if ($[2] !== notification || $[3] !== props.formState || $[4] !== props.i18n || $[5] !== props.subject || $[6] !== router || $[7] !== t0) {
      t1 = {
        ...t0,
        throwOnError: false,
        onSuccess(data) {
          notification.dispatchSuccess();
          props.formState.resetForm();
          const trackingData = {
            ...props.formState.values,
            subject: props.subject,
            dealID: data.id
          };
          GTM.setUserData(props.formState.values.email);
          GTM.sendEvent("funkeyContactFormSubmit", trackingData);
          posthog.capture("funkeyContactFormSubmit", trackingData);
          router.push(I18N_CONF.getTranslatedUrl("checkout", {
            locale: props.i18n.locale,
            slug: "confirmation"
          }));
        },
        onError(e) {
          notification.dispatchError({
            message: e.message
          });
        }
      };
      $[2] = notification;
      $[3] = props.formState;
      $[4] = props.i18n;
      $[5] = props.subject;
      $[6] = router;
      $[7] = t0;
      $[8] = t1;
    } else {
      t1 = $[8];
    }
    const createDeal = useMutation(t1);
    let t2;
    if ($[9] !== createDeal || $[10] !== props.formState || $[11] !== props.i18n) {
      t2 = async () => {
        if (!props.formState.validate()) {
          console.error(props.formState.errors);
          setTimeout(_temp3, 100);
          return;
        }
        return createDeal.mutateAsync({
          ...QuoteRequestFormFields.formValuesToCreateDealInput(props.formState.values),
          source: "CONTACT_FORM",
          locale: props.i18n.locale,
          items: null
        }).then(_temp4);
      };
      $[9] = createDeal;
      $[10] = props.formState;
      $[11] = props.i18n;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    let t3;
    if ($[13] !== props.formState || $[14] !== props.i18n) {
      t3 = <QuoteRequestFormFields.Component i18n={props.i18n} formState={props.formState} />;
      $[13] = props.formState;
      $[14] = props.i18n;
      $[15] = t3;
    } else {
      t3 = $[15];
    }
    const t4 = typeof props.formState.values.participants === "number" && props.formState.values.participants < 15;
    let t5;
    if ($[16] !== props.i18n || $[17] !== props.recaptcha || $[18] !== t4) {
      t5 = <ContactForm.Footer i18n={props.i18n} recaptcha={props.recaptcha} disabled={t4} />;
      $[16] = props.i18n;
      $[17] = props.recaptcha;
      $[18] = t4;
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    let t6;
    if ($[20] !== t2 || $[21] !== t3 || $[22] !== t5) {
      t6 = <ContactForm.Form onSubmit={t2}>{t3}{t5}</ContactForm.Form>;
      $[20] = t2;
      $[21] = t3;
      $[22] = t5;
      $[23] = t6;
    } else {
      t6 = $[23];
    }
    return t6;
  };
}
namespace SubjectTypes {
  export type Variant = 'Quote request' | 'Partnership question' | 'Teambuilding question' | 'Other question';
  function createMeta<T extends Record<string, unknown>>(meta: {
    label: string;
    Form: React.FC<{
      i18n: ReturnType<typeof useI18n>;
      variant: Variant;
      subject: string;
      recaptcha: ReturnType<typeof useReCAPTCHA>;
    }>;
  }) {
    return meta as typeof meta & {
      $type: T;
    };
  }
  export const VARIANT_META = (i18n: ReturnType<typeof useI18n>) => ({
    'Quote request': createMeta<QuoteRequestForm.FormValues>({
      label: i18n._('contact.form.subject.quote_request'),
      Form: props => {
        const formState = QuoteRequestFormFields.useState(i18n);
        return <QuoteRequestForm.Component {...props} formState={formState} />;
      }
    }),
    'Partnership question': createMeta<QuestionForm.FormValues>({
      label: i18n._('contact.form.subject.partnership_question'),
      Form: props => <QuestionForm.Component {...props} showCountryLocationField={false} />
    }),
    'Teambuilding question': createMeta<QuestionForm.FormValues>({
      label: i18n._('contact.form.subject.teambuilding_question'),
      Form: props => <QuestionForm.Component {...props} showCountryLocationField={true} />
    }),
    'Other question': createMeta<Omit<QuestionForm.FormValues, 'showCountryLocationField'>>({
      label: i18n._('contact.form.subject.other_question'),
      Form: props => <QuestionForm.Component {...props} showCountryLocationField={false} />
    })
  }) satisfies Record<Variant, ReturnType<typeof createMeta>>;
}
function _temp(el) {
  return {
    el,
    rect: el.getBoundingClientRect()
  };
}
function _temp2(a, b) {
  return a.rect.y - b.rect.y;
}
function _temp3() {
  const highestErrorLabel = Array.from(document.querySelectorAll("[data-testid=\"FormInputError\"]")).map(_temp).sort(_temp2)[0];
  if (!highestErrorLabel) {
    return;
  }
  window.scrollBy({
    top: highestErrorLabel.rect.top - 200,
    behavior: "smooth"
  });
}
function _temp4() {
  return sleep(200);
}